/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import EmassEnergyUsagesAllUtilitesDetials from './AllUtilitiesDetails/EmaasEnergyUsagesAllUtilitiesDetails';
import EmaasEnergyUsagesAllUtilitiesDetailsNILM from './AllUtilitiesDetails/EmaasEnergyUsagesAllUtilitiesDetailsNILM';
import EmassEnergyUsagesElectricityDetials from './ElectricityDetails/EmaasEnergyUsagesElectricityDetails';
import EmassEnergyUsagesGasDetials from './GasDetails/EmaasEnergyUsagesGasDetials';
import { ApiContext } from '../../../context/ApiContext';
import IEnergyManagementProps from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { renderEneryVDTabs } from '../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import EmaasEnergyDisaggregationDetails from './EnergyDisaggregationDetails/EmaasEnergyDisaggregationDetails';
import { useFilterScope1Context } from '../../../context/FilterScope1Context/FilterScope1Context';
import EmassEnergyUsagesWaterDetails from './WaterDetails/EmaasEnergyUsagesWaterDetails';
import { EnergyUsageDataSourceType } from '../../shared/Enums';
import EmassEnergyUsagesAllUtilitesDetialsLiveMeter from './AllUtilitiesDetails/EmassEnergyUsagesAllUtilitesDetialsLiveMeter';

const emaasEnergyUsagesViewDetailComponent = (
  props: IEnergyManagementProps
) => {
  const { requestContext, emaasContext, getAllMeterData, bearerToken } = props;
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  // const [state] = useFilterScope1Context();
  // const { subscriptionDetails } = state;
  const apiContext = useContext(ApiContext);
  const {
    onBacktoDashboardClick,
    enableConsumptionSourceSelect,
    cemsIntervalEnergySourceOption
  } = apiContext;
  const onBackarrowClick = () => {
    if (typeof onBacktoDashboardClick === 'function') {
      onBacktoDashboardClick();
    }
  };

  const getParams = new URLSearchParams(location.search);
  const tabName = getParams.get('tabName') || '';

  const [uComp, setUComp] = useState<boolean>(true);

  useEffect(() => {
    if (apiContext.dataSourceType === EnergyUsageDataSourceType.UTILITY_METER) {
      setUComp(true);
    } else {
      setUComp(false);
    }
  }, [apiContext.dataSourceType]);

  let energyDisaggregationTab: {
    className: string;
    title: string;
    component: (props: IEnergyManagementProps) => JSX.Element;
  }[];
  // Enable Disaggregation Tab for SmartImpulse NILM or Any other third party disaggregation integration
  // such as Redaptive or Connected Sockets
  if (
    emaasContext?.isNILMDisaggregationSubscribed ||
    // Check site has shows disaggregation capability
    // Based on subscriptions & their tiers verified in parent app
    emaasContext?.showsDisaggregation
  ) {
    energyDisaggregationTab = [
      {
        className: 'emaas-energy-view-details-electricity',
        title: t(d.ENERGYDISAGGREGATION),
        component: EmaasEnergyDisaggregationDetails
      }
    ];
  } else {
    energyDisaggregationTab = [];
  }
  let waterTab: {
    className: string;
    title: string;
    component: (props: IEnergyManagementProps) => JSX.Element;
  }[] = [];
  if (emaasContext?.enableWaterUsageDashboards) {
    waterTab = [
      {
        className: 'emaas-energy-view-details-gas',
        title: t(d.Water),
        component: EmassEnergyUsagesWaterDetails
      }
    ];
  }
  const displayTabs = {
    displayTabs: [
      {
        className: 'emaas-energy-view-details-all-utilities',
        title: emaasContext?.enableWaterUsageDashboards
          ? t(d.ALLEnergySources)
          : t(d.ALLUTILITIES),
        component: EmassEnergyUsagesAllUtilitesDetials
      },
      {
        className: 'emaas-energy-view-details-electricity',
        title: t(d.ELECTRICITY),
        component: EmassEnergyUsagesElectricityDetials
      },
      {
        className: 'emaas-energy-view-details-gas',
        title: t(d.GAS),
        component: EmassEnergyUsagesGasDetials
      },
      // Water Tab at index 3
      ...waterTab
    ],
    requestContext,
    emaasContext,
    ActiveIndex:
      tabName && waterTab.length > 0 && tabName === 'water'
        ? 3
        : apiContext.ActiveIndex,
    getAllMeterData,
    bearerToken,
    enableConsumptionSourceSelect,
    indexDisableEnergyDataSource: waterTab.length > 0 ? [3] : [],
    cemsIntervalEnergySourceOption
  };

  const displayNILMBaiscTabs = {
    displayTabs: [
      {
        className: 'emaas-energy-view-details-all-utilities',
        title: t(d.ENERGYUSAGE),
        component: EmaasEnergyUsagesAllUtilitiesDetailsNILM
      },
      ...energyDisaggregationTab
    ],
    requestContext,
    emaasContext,
    ActiveIndex: apiContext.ActiveIndex,
    getAllMeterData
  };

  const displayNILMPremiumTabs = {
    displayTabs: [
      {
        className: 'emaas-energy-view-details-all-utilities',
        title: emaasContext?.enableWaterUsageDashboards
          ? t(d.ALLEnergySources)
          : t(d.ALLUTILITIES),
        component: EmassEnergyUsagesAllUtilitesDetials
      },
      {
        className: 'emaas-energy-view-details-electricity',
        title: t(d.ELECTRICITY),
        component: EmassEnergyUsagesElectricityDetials
      },
      {
        className: 'emaas-energy-view-details-gas',
        title: t(d.GAS),
        component: EmassEnergyUsagesGasDetials
      },
      // Water Tab at index 3
      ...waterTab,
      ...energyDisaggregationTab
    ],
    requestContext,
    emaasContext,
    ActiveIndex:
      tabName && waterTab.length > 0 && tabName === 'water'
        ? 3
        : apiContext.ActiveIndex,
    enableConsumptionSourceSelect,
    indexDisableEnergyDataSource: [
      ...(waterTab.length > 0 ? [3] : []),
      ...(energyDisaggregationTab.length > 0 && waterTab.length > 0
        ? [4]
        : energyDisaggregationTab.length > 0
        ? [3]
        : [])
    ],
    getAllMeterData,
    cemsIntervalEnergySourceOption
  };
  let displayTab;
  //remove the requestContext.context.subscriptionType and add subscriptionDetails

  if (
    requestContext.context.subscriptionType === 'PREMIUM_SUBSCRIPTION' &&
    ((emaasContext?.toggleFeatureNILM2 === true &&
      emaasContext?.isNILMSubscribed === true) ||
      (emaasContext?.hasDisaggIntegration &&
        emaasContext?.toggleRedaptiveIntegration))
  ) {
    displayTab = displayNILMPremiumTabs;
  } else if (
    requestContext.context.subscriptionType === 'BASIC_SUBSCRIPTION' &&
    emaasContext?.toggleFeatureNILM2 === true &&
    emaasContext?.isNILMSubscribed === true
  ) {
    displayTab = displayNILMBaiscTabs;
  } else {
    displayTab = displayTabs;
  }

  return renderEneryVDTabs(
    props.connectorConfig,
    onBackarrowClick,
    displayTab,
    emaasContext?.enableWaterUsageDashboards
      ? t(d.CONSUMPTIONOVERVIEW)
      : t(d.ENERGYUSAGEOVERVIEW),
    apiContext.energyUsageMultipleCalls
  );
};

export default emaasEnergyUsagesViewDetailComponent;
